import React from 'react';

import Context from '../../../../context';
import values  from '../../../../values';
import utils   from '../../../../utils';
import api     from '../../../../api';

import { useQuery       } from 'react-query';
import { useFormik      } from 'formik';
import { useTranslation } from 'react-i18next';

import { IItem } from '../../../../types/common.interface';
import { OFFLINE_PAYER_TYPE, OFFLINE_PAYER_WAY } from '../../../../values/dropdownItems';

import {
  CustomView,
  CustomModal,
  CustomInput,
  TableHeader,
  TableLoading,
  TableNullData,
  CustomButton2,
  CustomRadioButtonGroup,
} from '../../../../components';

import * as Yup from 'yup';

import './index.scss';

const PayOffline: React.FC = (): React.ReactElement => {

  const {showModal, isLoading, setShowModal, selectedService, payFunction} = React.useContext(Context.ServiceScreen);

  const [selectedPayerWay, setSelectedPayerWay] = React.useState<IItem>();
  const [selectedFactor  , setSelectedFactor  ] = React.useState<string[]>([]);
  const [selectedPayer   , setSelectedPayer   ] = React.useState<IItem>(OFFLINE_PAYER_TYPE[0]);
  const [totalPrice      , setTotalPrice      ] = React.useState<number>();

  const [errors, setErrors] = React.useState({
    payerWay: '',
    payerType: '',
    factorList: '',
  });

  const { t } = useTranslation();

  const onSubmit = () => {
    let selectedErrors = { payerType: '', payerWay: '', factorList: '' };

    if (!selectedPayer) {
      selectedErrors = {
        ...selectedErrors,
        payerType: t('payer_type_is_required'),
      };
    }
    if (!selectedPayerWay) {
      selectedErrors = {
        ...selectedErrors,
        payerWay: t('pay_way_is_required'),
      };
    }
    if (selectedFactor.length === 0) {
      selectedErrors = {
        ...selectedErrors,
        factorList: t('factorlist_is_required'),
      };
    }
    setErrors(selectedErrors);
    formik.submitForm();
  };

  const formik = useFormik({
    initialValues: {
      fishNumber: "",
    },
    validationSchema: Yup.object({
      fishNumber: Yup.string().required(t('fishNumber_is_required')),
    }),
    onSubmit: (values) => {
      if (
        selectedPayer &&
        selectedPayerWay &&
        selectedFactor.length !== 0
      ) {
        payFunction!({
          fishId: values.fishNumber,
          payerType: selectedPayer?.value as string,
          factorList: selectedFactor,
          offlinePayType: selectedPayerWay?.value as string,
        });
      }
    },
  });

  const calculatePrice = (factors: any, type: IItem) => {
    let price = 0;
    factors?.forEach((item: any) => {price += item?.price});
    if (type?.value === "DRIVER") {price = (price * (totalShare as number)) / 100}
    setTotalPrice(price);
  };

  const allFactors = useQuery(
    ['PAY_FACTOR', values.useQueryKeys.getInvoices, selectedService?._id],
    () => api.get.common.findInvoices({ serviceId: selectedService?._id }),
    { enabled: showModal === 'PAY_OFFLINE' }
  );

  const financialGroup = selectedService?.financialGroupSchool;

  React.useEffect(() => {
    setSelectedFactor(
      allFactors?.data?.result?.factorsList?.map((item: any) => item?._id)
    );
  }, [allFactors?.data?.result?.factorsList?.length]);

  const share = financialGroup?.subscriptionAgent?.share;

  let totalShare: number = 0;

  totalShare =
    Number(share?.admin) +
    Number(share?.municipality) +
    Number(share?.superAgent) +
    Number(share?.tax);

  React.useEffect(() => {
    calculatePrice(
      allFactors?.data?.result?.factorsList,
      selectedPayer as IItem
    );
  }, [totalShare, allFactors.data]);

  const checkHandler = (factorId: string) => {
    let newFactorList: string[] = [];
    if (selectedFactor?.find((item) => item === factorId)) {
      newFactorList = selectedFactor.filter((item) => item !== factorId);
    } else {
      newFactorList = [...selectedFactor, factorId];
      setErrors({ ...errors, factorList: "" });
    }

    setSelectedFactor(newFactorList);
    calculatePrice(
      allFactors?.data?.result?.factorsList?.filter((item: any) =>
        newFactorList?.includes(item?._id)
      ),
      selectedPayer as IItem
    );
  };

  return (
    <section id="service-factor">
      <CustomModal
        onClose={() => setShowModal!("NONE")}
        open={showModal === "PAY_OFFLINE"}
      >
        <div className="table-content-wrapper width-full">
          <table className="dropdown width-full">
            <TableHeader headers={values.tableHeaders.PAY_SERVICE_HEADER_TABLE}/>
            {allFactors.status === "loading" ? (
              <TableLoading />
            ) : (
              <>
                {(allFactors?.data?.result?.factorsList <= 0 ||
                  !allFactors?.data?.result?.factorsList) && (
                  <TableNullData text={t("driver_has_not_active_service")} />
                )}
                <tbody id="driver-table-body-container">
                  {allFactors?.data?.result?.factorsList?.map(
                    (factor: any, index: number) => {
                      return (
                        <tr key={`driver-${index}`} onClick={() => checkHandler(factor?._id)}>
                          <td>
                            <input id="checkmark" type="checkbox" name="checkmark"
                              checked={!!selectedFactor?.find((item) => item === factor._id)}
                            />
                          </td>
                          <td>{utils.convertGregorianToJalali({date: factor?.oldSubscriptionDate})}</td>
                          <td>{utils.convertGregorianToJalali({date: factor?.newSubscriptionDate})}</td>
                          <td className="pointer">
                            {factor?.price && (<div>{utils?.convertNumberToPrice(factor?.price)}{" "}{t("toman")}</div>)}
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
                <div className="error">{errors.factorList !== "" && errors.factorList}</div>
              </>
            )}
          </table>
        </div>

        <form>
          <CustomView.Container className="approve-form-content">
            <>
              <CustomView.Row className="radio-button-group">
                <CustomView.Column medium={12} className="form-colum dropdown">
                  <div className="form-colum">{t("payer")}</div>
                  <CustomRadioButtonGroup
                    menus={OFFLINE_PAYER_TYPE}
                    selected={selectedPayer as IItem}
                    onClick={(event: IItem | null) => {
                      setSelectedPayer(event as IItem);
                      setErrors({ ...errors, payerType: "" });
                      calculatePrice(
                        allFactors?.data?.result?.factorsList?.filter(
                          (item: any) => selectedFactor?.includes(item?._id)
                        ),
                        event as IItem
                      );
                    }}
                    canNull={false}
                  />
                  <div className="error">{errors.payerType !== "" && errors.payerType}</div>
                </CustomView.Column>
                <CustomView.Column medium={12} className="form-colum dropdown">
                  <div className="form-colum">{t("payWay")}</div>
                  <CustomRadioButtonGroup
                    menus={OFFLINE_PAYER_WAY}
                    selected={selectedPayerWay as IItem}
                    onClick={(event: IItem | null) => {
                      setSelectedPayerWay(event as IItem);
                      setErrors({ ...errors, payerWay: "" });
                    }}
                    canNull={false}
                  />
                  <div className="error">{errors.payerWay !== "" && errors.payerWay}</div>
                </CustomView.Column>
                <CustomView.Column medium={6} className="form-colum">
                  <CustomInput
                    id="price"
                    name="price"
                    label={t('price')}
                    value={utils.convertNumberToPrice(totalPrice as number) + t('toman')}
                    formikProps={{ onBlur: formik.handleBlur }}
                    isDisable
                  />
                </CustomView.Column>
                <CustomView.Column medium={6} className="form-colum">
                  <CustomInput
                    name="fishNumber"
                    id="fishNumber"
                    label={t("fishNumber")}
                    value={formik.values.fishNumber}
                    onValueChange={formik.handleChange}
                    formikProps={{ onBlur: formik.handleBlur }}
                    error={formik.errors.fishNumber && formik.touched.fishNumber}
                    errorText={formik.errors.fishNumber}
                  />
                </CustomView.Column>
              </CustomView.Row>
              <br />
            </>
            <CustomView.Row className="form-row">
              <CustomView.Column large={3} medium={4} small={6} className="form-colum">
                <CustomButton2 isLoading={isLoading} onClick={() => setShowModal!("NONE")} title={t("cancel")} type="danger"/>
              </CustomView.Column>
              <CustomView.Column large={3} medium={4} small={6} className="form-colum">
                <CustomButton2 isLoading={isLoading} onClick={onSubmit} title={t("approve")} type={"primary"}/>
              </CustomView.Column>
            </CustomView.Row>
          </CustomView.Container>
        </form>
      </CustomModal>
    </section>
  );
};
export default PayOffline;
